import { View } from "@/components/ui";
import { useState } from "react";
import type { ComponentProps } from "react";
import { Typography } from "../index";
import { cn } from "@/lib/utils";
import { useComponentHeight } from "@/components/hooks/use-component-height";
import type { ExpandableTextProps as BaseExpandableTextProps } from "./type";

export type ExpandableTextProps = BaseExpandableTextProps &
  Omit<ComponentProps<typeof Typography>, "children">;

export function ExpandableText({
  children,
  className,
  ...typographyProps
}: ExpandableTextProps) {
  const [expanded, setExpanded] = useState(false);
  const fullHeight = useComponentHeight();
  const clampedHeight = useComponentHeight();
  const showToggle =
    fullHeight.height > 0 &&
    clampedHeight.height > 0 &&
    fullHeight.height > clampedHeight.height;
  return (
    <View className="relative w-full overflow-hidden">
      <div
        className="pointer-events-none absolute left-0 top-0 w-full opacity-0"
        aria-hidden
      >
        <div ref={fullHeight.ref}>
          <Typography {...typographyProps} numberOfLines={undefined}>
            {children}
          </Typography>
        </div>
        <div ref={clampedHeight.ref}>
          <Typography
            {...typographyProps}
            numberOfLines={3}
            className={cn("line-clamp-3")}
          >
            {children}
          </Typography>
        </div>
      </div>
      <Typography
        numberOfLines={expanded ? undefined : 3}
        className={cn(!expanded && "line-clamp-3", className)}
        {...typographyProps}
      >
        {children}
      </Typography>
      {showToggle ? (
        <Typography
          variant="caption-md"
          color="brand-primary"
          className="native:active:opacity-70 cursor-pointer"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Read less" : "Read more"}
        </Typography>
      ) : null}
    </View>
  );
}
